/**
 * Єдина матриця прав меню (Ролі + Персонал).
 * Ключ зберігається в Role.permissions / Staff.permissions.
 *
 * Семантика в рантаймі (Layout, permissions.js, StockHub):
 * доступ лише якщо effective_permissions[key] === true.
 * Відсутній ключ або false = модуля немає в меню і за прямим URL.
 *
 * Позначка requireTrue у матриці — підказка для UI («лише з галочкою»),
 * щоб клієнт явно вмикав чутливі модулі (Фінанси, LMS, блокування…).
 */
export const PERMISSION_MATRIX = [
  {
    section: 'Головна',
    permissions: [
      { key: 'menu.dashboard', label: 'Головна панель' },
      { key: 'menu.shift_floor', label: 'Моніторинг цеху (начальник зміни)' },
    ],
  },
  {
    section: 'Задачі',
    permissions: [
      { key: 'menu.tasks', label: 'Задачі (Synvolve Tasks): працювати в проектах, куди додали' },
      {
        key: 'menu.tasks.manage',
        label: 'Задачі: створювати проекти (менеджер) — лише з галочкою',
        requireTrue: true,
      },
    ],
  },
  {
    section: 'Замовлення',
    permissions: [
      { key: 'menu.customer_orders', label: 'Замовлення та замовники' },
    ],
  },
  {
    section: 'Закупівлі',
    permissions: [
      { key: 'menu.purchases', label: 'Закупівлі та постачальники (заявки; Web Push при новій заявці)' },
      {
        key: 'menu.purchase_approvals',
        label: 'Погодження закупівель (окрема вкладка, бейдж очікуваних) — лише з галочкою',
        requireTrue: true,
      },
    ],
  },
  {
    section: 'Виробництво',
    permissions: [
      { key: 'menu.production', label: 'Розділ «Виробництво»' },
      { key: 'menu.production.planner', label: 'Планер' },
      { key: 'menu.production.operator', label: 'Робоче місце оператора' },
      { key: 'menu.production.warehouse', label: 'Склад виробництва' },
      {
        key: 'menu.production.warehouse.defect',
        label: 'Брак на складі виробництва (вкладка «Брак», відправка на дробарку) — лише з галочкою',
        requireTrue: true,
      },
      { key: 'menu.production.work_processes', label: 'Робочі процеси' },
      { key: 'menu.production.setup', label: 'Наладка' },
      { key: 'menu.production.qc_kanban', label: 'Контроль якості (Канбан ВКЯ)' },
      { key: 'menu.production.qc_stock', label: 'Склад контролю якості' },
      { key: 'menu.production.drying', label: 'Сушіння (черга матеріалів)' },
      { key: 'menu.production.mixing', label: 'Лінія змішування сировини' },
      { key: 'menu.crushing', label: 'Дроблення (канбан)' },
      { key: 'menu.production.maintenance', label: 'План обслуговування / канбан ТО' },
      { key: 'menu.production.products', label: 'Вироби та комплекти виробів' },
      { key: 'menu.production.machines', label: 'ТПА' },
      { key: 'menu.production.pressforms', label: 'Прес-форми' },
    ],
  },
  {
    section: 'Упаковка',
    permissions: [
      { key: 'menu.packaging', label: 'Упаковка / зона пакування (роль Пакувальник)' },
      {
        key: 'menu.packaging_daily_norm',
        label: 'Норма пакування на день (менеджер упаковки) — лише з галочкою',
        requireTrue: true,
      },
    ],
  },
  {
    section: 'Склади',
    permissions: [
      { key: 'menu.stock', label: 'Розділ «Склади» (хаб)' },
      { key: 'menu.stock.raw', label: 'Склад сировини' },
      { key: 'menu.stock.dyes', label: 'Склад барвників' },
      { key: 'menu.stock.packaging', label: 'Склад упаковки' },
      { key: 'menu.stock.technical', label: 'Склад техматеріалів' },
      { key: 'menu.stock.semi', label: 'Склад напівфабрикатів' },
      { key: 'menu.stock.packaging_work', label: 'Склад для пакування' },
      { key: 'menu.stock.finished', label: 'Склад готової продукції' },
      { key: 'menu.stock.finished.manual_receipt', label: 'Ручний прихід ГП (вільний залишок)' },
      { key: 'menu.stock.crushing_waste', label: 'Склад перемолів' },
      {
        key: 'menu.stock.blocking',
        label: 'Склад блокування (перегляд) — лише з галочкою',
        requireTrue: true,
      },
      {
        key: 'menu.stock.blocking.resolve',
        label: 'Рішення по блокуванню / тимчасовий допуск до відвантаження (ВКЯ) — лише з галочкою',
        requireTrue: true,
      },
      { key: 'menu.stock.consumable', label: 'Склад розхідних матеріалів' },
      { key: 'menu.stock.samples', label: 'Склад взірців' },
      { key: 'menu.stock.movements', label: 'Журнал рухів складу' },
      { key: 'menu.stock.material_prices', label: 'Довідник цін матеріалів' },
    ],
  },
  {
    section: 'CRM',
    permissions: [
      {
        key: 'menu.crm',
        label: 'CRM (Twenty, вбудована) — лише з галочкою',
        requireTrue: true,
      },
    ],
  },
  {
    section: 'Навчання NEW',
    permissions: [
      {
        key: 'menu.learning_new',
        label: 'Навчання NEW: курси й проходження тестів — лише з галочкою',
        requireTrue: true,
      },
      {
        key: 'menu.learning_new.manage',
        label: 'Навчання NEW: створення курсів і тестів, усі результати — лише з галочкою',
        requireTrue: true,
      },
    ],
  },
  {
    section: 'Навчання (LMS)',
    permissions: [
      {
        key: 'menu.lms',
        label: 'Навчання (курси, тести, результати) — лише з галочкою',
        requireTrue: true,
      },
      {
        key: 'menu.lms.manage',
        label: 'Керування навчанням (редагування, матеріали, дашборд, AI / Sync) — лише з галочкою',
        requireTrue: true,
      },
    ],
  },
  {
    section: 'Налаштування',
    permissions: [
      { key: 'menu.settings', label: 'Розділ «Налаштування»' },
      { key: 'menu.settings.roles', label: 'Ролі' },
      { key: 'menu.settings.staff', label: 'Персонал' },
      { key: 'menu.settings.quality', label: 'ВКЯ (налаштування якості)' },
      { key: 'menu.settings.order_periods', label: 'Звітні періоди замовлень' },
      { key: 'menu.export', label: 'Експорт' },
      { key: 'menu.import', label: 'Імпорт' },
    ],
  },
  {
    section: 'Ліди, Проекти, R&D, OEE, Фінанси',
    permissions: [
      { key: 'menu.leads', label: 'Ліди' },
      { key: 'menu.projects', label: 'Проекти' },
      { key: 'menu.rnd', label: 'R&D (дослідження та розробки)' },
      { key: 'menu.oee', label: 'OEE (ефективність обладнання)' },
      {
        key: 'menu.costing',
        label: 'Собівартість (калькулятор) — лише з галочкою',
        requireTrue: true,
      },
      {
        key: 'menu.finance',
        label: 'Фінанси (Cash Flow) — лише з галочкою',
        requireTrue: true,
      },
    ],
  },
  {
    section: 'Журнали та інструменти',
    permissions: [
      { key: 'menu.activity_log', label: 'Журнал подій' },
      { key: 'menu.notification_rules', label: 'Правила сповіщень' },
      { key: 'menu.process_log', label: 'Журнал процесів' },
      { key: 'menu.bulk_edit', label: 'Масове редагування' },
    ],
  },
];

export function getAllPermissionKeys() {
  return PERMISSION_MATRIX.flatMap((s) => s.permissions.map((p) => p.key));
}
